import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Settings as SettingsIcon, ToggleLeft, ToggleRight, Plus, Trash2, Save, Loader2, ListChecks, Zap, ChevronDown, ChevronUp, Megaphone, ShieldCheck, Sparkles, BellRing } from "lucide-react";
import { API, authHeaders, ROLE_OPTIONS } from "@/constants/roles";
import { renderAnnouncementText } from "@/components/dashboard/Announcement";
import { GuidelinesCard } from "@/components/admin/GuidelinesCard";
import { CapacityCard } from "@/components/admin/CapacityCard";
import { HiringRefsCard } from "@/components/admin/HiringRefsCard";
import { ChangePasswordCard } from "@/components/dashboard/ChangePasswordCard";
import { DemoEnvironmentCard } from "@/components/admin/DemoEnvironmentCard";
import { CurrencyCard } from "@/components/admin/CurrencyCard";
import { ShowcaseManager } from "@/components/admin/ShowcaseManager";

const formatError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return "Something went wrong.";
};

const EMPTY_ITEM = { title: "", body: "", auto: false, audience: "all" };

const GROUP_LABELS = {
  production: "Production",
  team: "Team",
  hiring: "Hiring",
  finance: "Finance",
  system: "System",
};

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const DigestScheduleCard = () => {
  const [cfg, setCfg] = useState(null);
  const [day, setDay] = useState(1);
  const [hour, setHour] = useState(9);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/digest-settings`, { headers: authHeaders() })
      .then((r) => { setCfg(r.data); setDay(r.data.day); setHour(r.data.hour); })
      .catch(() => toast.error("Could not load digest schedule."));
  }, []);

  const dirty = cfg && (cfg.day !== day || cfg.hour !== hour);
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/digest-settings`, { day, hour }, { headers: authHeaders() });
      setCfg((c) => ({ ...c, day, hour }));
      toast.success(`Digest schedule saved — sends ${DAY_NAMES[day]}s at ${String(hour).padStart(2, "0")}:00 UTC.`);
    } catch { toast.error("Could not save the digest schedule."); } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-3xl border border-emerald-500/20 bg-[var(--pv-card)] p-6 sm:p-8" data-testid="digest-schedule-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-5 w-5 text-emerald-400" />
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Weekly hits digest</h2>
        </div>
        {dirty && (
          <button data-testid="digest-schedule-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-600 disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save schedule
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-[var(--pv-text-4)]">
        When the owner digest email lands each week — biggest view and revenue climbers across YouTube-linked productions. Times are UTC. Applies from the next scheduled week; emails deliver on the live site only. Preview anytime from Videos → Preview digest.
      </p>
      {!cfg ? (
        <p data-testid="digest-schedule-loading" className="py-6 text-center text-sm text-[var(--pv-text-4)]">Loading schedule...</p>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-[var(--pv-text-3)]">Every</span>
          <select data-testid="digest-day-select" value={day} onChange={(e) => setDay(parseInt(e.target.value))} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--pv-text)] focus:border-emerald-400 focus:outline-none">
            {DAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
          <span className="text-xs font-semibold text-[var(--pv-text-3)]">at</span>
          <select data-testid="digest-hour-select" value={hour} onChange={(e) => setHour(parseInt(e.target.value))} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--pv-text)] focus:border-emerald-400 focus:outline-none">
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>)}
          </select>
          {cfg.last_sent_week && <span data-testid="digest-last-sent" className="text-[11px] text-[var(--pv-text-4)]">Last sent: week {cfg.last_sent_week}</span>}
        </div>
      )}
    </div>
  );
};

const NudgeSettingsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/nudge-settings`, { headers: authHeaders() })
      .then((r) => setCfg(r.data))
      .catch(() => toast.error("Could not load nudge settings."));
  }, []);
  const toggle = async () => {
    if (!cfg || saving) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/nudge-settings`, { enabled: !cfg.enabled }, { headers: authHeaders() });
      setCfg((c) => ({ ...c, enabled: data.enabled }));
      toast.success(data.enabled ? "Auto-nudge on — overdue jobs get one reminder per day." : "Auto-nudge off — only manual nudges.");
    } catch { toast.error("Could not save nudge settings."); } finally { setSaving(false); }
  };
  return (
    <div className="mt-6 rounded-3xl border border-amber-500/20 bg-[var(--pv-card)] p-6 sm:p-8" data-testid="nudge-settings-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <BellRing className="h-5 w-5 text-amber-400" />
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Auto-nudge overdue jobs</h2>
        </div>
        <button
          data-testid="nudge-auto-toggle"
          onClick={toggle}
          disabled={!cfg || saving}
          className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors disabled:opacity-50 ${cfg?.enabled ? "border-amber-500/40 bg-amber-500/15 text-amber-300" : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)]"}`}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : cfg?.enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
          {cfg ? (cfg.enabled ? "On" : "Off") : "…"}
        </button>
      </div>
      <p className="mt-2 text-xs text-[var(--pv-text-4)]">
        When a claimed job passes 24 hours overdue, the daily deadline run automatically reminds the worker in-site and in their Production Manager Team chat. The same 20-hour cooldown as manual nudges applies, so reminders never pile up — and nobody is ever penalized automatically.
      </p>
    </div>
  );
};

const PermissionsCard = () => {
  const [groups, setGroups] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/permissions`, { headers: authHeaders() })
      .then((r) => setGroups(r.data.groups))
      .catch(() => toast.error("Could not load permissions."));
  }, []);

  const toggle = (key, enabled) => {
    setGroups((g) => ({ ...g, ...Object.fromEntries(Object.entries(g).map(([gn, items]) => [gn, items.map((i) => (i.key === key ? { ...i, enabled } : i))])) }));
    setDirty((d) => ({ ...d, [key]: enabled }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const all = {};
      Object.values(groups).forEach((items) => items.forEach((i) => { all[i.key] = i.enabled; }));
      await axios.put(`${API}/admin/permissions`, { permissions: all }, { headers: authHeaders() });
      setDirty({});
      toast.success("Manager permissions updated — they apply immediately.");
    } catch {
      toast.error("Could not save permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 rounded-3xl border border-violet-500/20 bg-[var(--pv-card)] p-6 sm:p-8" data-testid="permissions-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="h-5 w-5 text-violet-400" />
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Production Manager permissions</h2>
        </div>
        {Object.keys(dirty).length > 0 && (
          <button data-testid="permissions-save-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-full bg-violet-500 px-4 py-2 text-xs font-bold text-white hover:bg-violet-600 disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save changes ({Object.keys(dirty).length})
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-[var(--pv-text-4)]">
        Exactly what a Production Manager can do. Only you (the owner) can change these. Payments, budget, hiring decisions and system settings stay owner-only unless you explicitly enable them here.
      </p>
      {!groups ? (
        <p data-testid="permissions-loading" className="py-8 text-center text-sm text-[var(--pv-text-4)]">Loading permissions...</p>
      ) : (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group} data-testid={`perm-group-${group}`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-violet-300">{GROUP_LABELS[group] || group}</p>
              <div className="mt-3 space-y-2">
                {items.map((i) => (
                  <button
                    key={i.key}
                    data-testid={`perm-toggle-${i.key}`}
                    onClick={() => toggle(i.key, !i.enabled)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-[var(--pv-card)]"
                  >
                    <span className="text-xs font-medium text-[var(--pv-text-2)]">{i.label}</span>
                    {i.enabled ? <ToggleRight className="h-5 w-5 shrink-0 text-violet-400" /> : <ToggleLeft className="h-5 w-5 shrink-0 text-[var(--pv-text-4)]" />}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminSettings = ({ me }) => {
  const [roles, setRoles] = useState([]);
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState(EMPTY_ITEM);
  const [adding, setAdding] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_ITEM);
  const [savingId, setSavingId] = useState(null);
  const [announcement, setAnnouncement] = useState({ enabled: false, text: "" });
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/roles`),
      axios.get(`${API}/admin/onboarding/items`, { headers: authHeaders() }),
      axios.get(`${API}/announcement`, { headers: authHeaders() }),
    ])
      .then(([r, i, a]) => {
        setRoles(r.data);
        setItems(i.data);
        setAnnouncement({ enabled: a.data.enabled, text: a.data.text || "" });
      })
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else toast.error("Could not load settings.");
      });
  }, [navigate]);

  useEffect(load, [load]);

  const saveAnnouncement = async (enabled) => {
    setSavingAnnouncement(true);
    try {
      await axios.put(`${API}/admin/announcement`, { enabled, text: announcement.text }, { headers: authHeaders() });
      setAnnouncement((a) => ({ ...a, enabled }));
      toast.success(enabled ? "Announcement is live on every worker dashboard." : "Announcement turned off.");
    } catch {
      toast.error("Could not save announcement.");
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const toggleRole = async (role, open) => {
    try {
      await axios.patch(`${API}/roles/status`, { role, open }, { headers: authHeaders() });
      setRoles((rs) => rs.map((r) => (r.role === role ? { ...r, open } : r)));
      toast.success(`${role} applications ${open ? "opened" : "closed"}.`);
    } catch {
      toast.error("Could not update role status.");
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    setSavingNew(true);
    try {
      await axios.post(
        `${API}/admin/onboarding/items`,
        { title: draft.title, body: draft.body, auto: draft.auto ? "first_job" : null, audience: draft.audience || "all" },
        { headers: authHeaders() }
      );
      toast.success("Checklist item added.");
      setDraft(EMPTY_ITEM);
      setAdding(false);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingNew(false);
    }
  };

  const saveItem = async (id) => {
    setSavingId(id);
    try {
      await axios.patch(
        `${API}/admin/onboarding/items/${id}`,
        { title: editDraft.title, body: editDraft.body, auto: editDraft.auto ? "first_job" : "", audience: editDraft.audience || "all" },
        { headers: authHeaders() }
      );
      toast.success("Item updated.");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/admin/onboarding/items/${id}`, { headers: authHeaders() });
      toast.success("Item removed.");
      load();
    } catch {
      toast.error("Could not delete item.");
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

  const itemForm = (d, setD, onSubmit, busy, submitLabel, testPrefix) => (
    <form data-testid={`${testPrefix}-form`} onSubmit={onSubmit} className="mt-3 space-y-3 rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-4">
      <input
        data-testid={`${testPrefix}-title`}
        required
        minLength={3}
        value={d.title}
        onChange={(e) => setD({ ...d, title: e.target.value })}
        placeholder="Checklist item title"
        className={inputCls}
      />
      <textarea
        data-testid={`${testPrefix}-body`}
        rows={3}
        value={d.body}
        onChange={(e) => setD({ ...d, body: e.target.value })}
        placeholder="Content the worker reads when they open this item (kept on the site — no external links needed)"
        className={inputCls}
      />
      <label className="flex items-center gap-2 text-xs font-semibold text-[var(--pv-text-4)]">
        <input
          data-testid={`${testPrefix}-auto`}
          type="checkbox"
          checked={d.auto}
          onChange={(e) => setD({ ...d, auto: e.target.checked })}
          className="h-4 w-4 rounded border-[var(--pv-border-strong)] text-cyan-500 focus:ring-cyan-500"
        />
        <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-amber-500" /> Auto-complete when worker finishes their first job</span>
      </label>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-[var(--pv-text-4)]">Show to:</span>
        <select
          data-testid={`${testPrefix}-audience`}
          value={d.audience || "all"}
          onChange={(e) => setD({ ...d, audience: e.target.value })}
          className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none"
        >
          <option value="all">All roles</option>
          {ROLE_OPTIONS.filter((r) => r !== "General / Multiple Roles").map((r) => <option key={r} value={r}>{r} only</option>)}
        </select>
      </div>
      <button
        data-testid={`${testPrefix}-save`}
        type="submit"
        disabled={busy}
        className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cyan-600 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        {submitLabel}
      </button>
    </form>
  );

  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Settings</h1>
      <p className="mt-1 text-sm text-[var(--pv-text-4)]">Control which roles accept applications and what new workers must complete.</p>

      <div className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6  sm:p-8">
        <div className="flex items-center gap-2.5">
          <SettingsIcon className="h-5 w-5 text-cyan-400" />
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Role availability</h2>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {roles.map((r) => (
            <button
              key={r.role}
              data-testid={`settings-role-toggle-${r.role.toLowerCase().replace(/[\s/]+/g, "-")}`}
              onClick={() => toggleRole(r.role, !r.open)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                r.open
                  ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
                  : "border-rose-500/20 bg-rose-500/10 text-rose-400 hover:bg-rose-500/15"
              }`}
            >
              {r.open ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
              {r.role} · {r.open ? "Open" : "Closed"}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-[var(--pv-text-4)]">Closed roles immediately stop accepting new applications on the site.</p>
      </div>

      <PermissionsCard />

      {!me?.demo && (
        <div className="mt-6">
          <CurrencyCard />
        </div>
      )}

      {!me?.demo && <DigestScheduleCard />}

      {!me?.demo && <NudgeSettingsCard />}

      {!me?.demo && (
        <div className="mt-6">
          <ShowcaseManager />
        </div>
      )}

      {!me?.demo && (
        <div className="mt-6">
          <ChangePasswordCard testid="admin-change-password" title="Owner account — change password" />
        </div>
      )}

      <DemoEnvironmentCard />

      <div className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Megaphone className="h-5 w-5 text-amber-400" />
            <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Worker announcement banner</h2>
          </div>
          <button
            data-testid="announcement-toggle-btn"
            onClick={() => saveAnnouncement(!announcement.enabled)}
            disabled={savingAnnouncement}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors disabled:opacity-60 ${
              announcement.enabled
                ? "border-amber-500/30 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
                : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)] hover:text-[var(--pv-text)]"
            }`}
          >
            {announcement.enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
            {announcement.enabled ? "Live for all workers" : "Off"}
          </button>
        </div>
        <p className="mt-2 text-xs text-[var(--pv-text-4)]">
          Shows as a big yellow banner at the top of every worker dashboard. Use **double asterisks** for bold and line breaks for paragraphs. Workers see changes automatically.
        </p>
        <textarea
          data-testid="announcement-text-input"
          rows={4}
          value={announcement.text}
          onChange={(e) => setAnnouncement((a) => ({ ...a, text: e.target.value }))}
          placeholder={"e.g. **Production is starting now!** Claim any available job.\nPayment may be delayed until the end of the month in the worst case while the payment setup finishes."}
          className="mt-4 w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-amber-400 focus:outline-none focus:ring-4 focus:ring-amber-500/10"
        />
        {announcement.text.trim() && (
          <div className="mt-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Preview</p>
            <div data-testid="announcement-preview" className="mt-2 rounded-2xl border border-amber-500/40 bg-amber-400 px-5 py-4">
              <p className="text-sm font-medium leading-relaxed text-amber-950">{renderAnnouncementText(announcement.text)}</p>
            </div>
          </div>
        )}
        <button
          data-testid="announcement-save-btn"
          onClick={() => saveAnnouncement(announcement.enabled)}
          disabled={savingAnnouncement}
          className="mt-4 flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-60"
        >
          {savingAnnouncement ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save message
        </button>
      </div>

      <div data-testid="onboarding-checklist-card" className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6  sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <ListChecks className="h-5 w-5 text-cyan-400" />
            <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Worker onboarding checklist</h2>
          </div>
          <button
            data-testid="onboarding-add-btn"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600"
          >
            <Plus className="h-3.5 w-3.5" /> {adding ? "Cancel" : "Add item"}
          </button>
        </div>
        <p className="mt-2 text-xs text-[var(--pv-text-4)]">Every new worker sees this checklist on their dashboard. All content lives on the site — no external apps required.</p>

        {adding && itemForm(draft, setDraft, addItem, savingNew, "Add item", "onboarding-new")}

        <div className="mt-5 space-y-3">
          {!items ? (
            <p data-testid="onboarding-loading" className="py-8 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading checklist...</p>
          ) : items.length === 0 ? (
            <p data-testid="onboarding-empty" className="rounded-2xl border border-dashed border-[var(--pv-border-strong)] py-8 text-center text-sm text-[var(--pv-text-4)]">No checklist items — add the first one.</p>
          ) : (
            items.map((item, i) => (
              <div key={item.id} data-testid={`onboarding-item-${item.id}`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--pv-card)] text-[10px] font-bold text-[var(--pv-text)]">{i + 1}</span>
                    <p className="text-sm font-bold text-[var(--pv-text)]">{item.title}</p>
                    {item.auto === "first_job" && (
                      <span className="flex items-center gap-1 rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                        <Zap className="h-3 w-3" /> Auto
                      </span>
                    )}
                    {item.audience && item.audience !== "all" && (
                      <span data-testid={`onboarding-audience-${item.id}`} className="rounded-full border border-violet-500/25 bg-violet-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-300">
                        {item.audience} only
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`onboarding-edit-${item.id}`}
                      onClick={() => {
                        setEditId(editId === item.id ? null : item.id);
                        setEditDraft({ title: item.title, body: item.body || "", auto: item.auto === "first_job", audience: item.audience || "all" });
                      }}
                      className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3 py-1.5 text-xs font-semibold text-[var(--pv-text-4)] hover:border-cyan-500/30"
                    >
                      {editId === item.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      Edit
                    </button>
                    <button
                      data-testid={`onboarding-delete-${item.id}`}
                      onClick={() => deleteItem(item.id)}
                      className="rounded-full bg-rose-500/10 p-2 text-rose-500 transition-colors hover:bg-rose-500/15"
                      aria-label="Delete item"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {item.body && editId !== item.id && <p className="mt-2 pl-9 text-xs leading-relaxed text-[var(--pv-text-4)]">{item.body}</p>}
                {editId === item.id && itemForm(editDraft, setEditDraft, (e) => { e.preventDefault(); saveItem(item.id); }, savingId === item.id, "Save changes", `onboarding-edit-form-${item.id}`)}
              </div>
            ))
          )}
        </div>
      </div>

      <GuidelinesCard />

      <HiringRefsCard />

      <CapacityCard />
    </div>
  );
};
